import React, { useEffect, useMemo, useState, useCallback } from 'react';

export default function Team({ role }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [busyEmail, setBusyEmail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/get_users.php', { credentials: 'include' });
      const data = await res.json();
      setRows(Array.isArray(data?.data) ? data.data : []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => { await load(); })();
  }, [load]);

  async function addUser() {
    const name = window.prompt('Name:');
    if (name === null) return;
    const email = window.prompt('Email:');
    if (email === null) return;
    const role = window.prompt('Role (admin | staff | user):', 'user');
    if (role === null) return;
    setBusyEmail(email);
    try {
      await fetch('/api/manage_users.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', user: { name, email, role } })
      });
      await load();
    } finally {
      setBusyEmail(null);
    }
  }

  async function editUser(u) {
    const name = window.prompt('Name:', u.name || '');
    if (name === null) return;
    const role = window.prompt('Role (admin | staff | user):', u.role || 'user');
    if (role === null) return;
    const status = window.prompt('Status (active | inactive):', u.status || 'active');
    if (status === null) return;
    setBusyEmail(u.email);
    try {
      await fetch('/api/manage_users.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'update', user: { email: u.email, name, role, status } })
      });
      await load();
    } finally {
      setBusyEmail(null);
    }
  }

  async function deleteUser(email) {
    if (!window.confirm(`Delete user ${email}?`)) return;
    setBusyEmail(email);
    try {
      await fetch('/api/manage_users.php', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', email })
      });
      await load();
    } finally {
      setBusyEmail(null);
    }
  }

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (roleFilter && String(r.role || '').toLowerCase() !== roleFilter) return false;
      if (!q) return true;
      return [r.name, r.email].some((v) => String(v || '').toLowerCase().includes(q));
    });
  }, [rows, query, roleFilter]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Team</h1>
        <div className="flex items-center gap-2">
          <input className="h-9 px-3 border rounded-md" placeholder="Search name/email" value={query} onChange={(e) => setQuery(e.target.value)} aria-label="Search users" />
          <select className="h-9 px-2 border rounded-md" aria-label="Filter by role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">All roles</option>
            <option value="admin">Admin</option>
            <option value="staff">Staff</option>
            <option value="user">User</option>
          </select>
          <button onClick={addUser} className="h-9 px-3 rounded-md bg-gray-900 text-white" aria-label="Add user">Add user</button>
        </div>
      </div>

      <div className="rounded-lg border overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Role</Th>
              <Th>Status</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td className="p-3" colSpan={5}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td className="p-3" colSpan={5}>No users found.</td></tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="border-t hover:bg-gray-50">
                  <Td>{r.name || '—'}</Td>
                  <Td>{r.email}</Td>
                  <Td><Badge>{r.role || 'user'}</Badge></Td>
                  <Td><Badge tone={r.status === 'active' ? 'green' : 'gray'}>{r.status || 'active'}</Badge></Td>
                  <Td className="text-right">
                    <button disabled={busyEmail===r.email} onClick={() => editUser(r)} className="text-blue-600 hover:underline mr-3 disabled:opacity-50" aria-label={`Edit user ${r.email}`}>Edit</button>
                    <button disabled={busyEmail===r.email} onClick={() => deleteUser(r.email)} className="text-red-600 hover:underline disabled:opacity-50" aria-label={`Delete user ${r.email}`}>Delete</button>
                  </Td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Th({ children, className = '' }) {
  return <th className={`p-3 font-medium text-left ${className}`}>{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`p-3 align-top ${className}`}>{children}</td>;
}

function Badge({ children, tone = 'gray' }) {
  const tones = {
    gray: 'bg-gray-100 text-gray-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    blue: 'bg-blue-100 text-blue-700',
  };
  return <span className={`px-2 py-0.5 rounded text-xs ${tones[tone]}`}>{children}</span>;
}
